import React, { useState, useEffect } from "react";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { 
  Edit,
  Trash2,
  MoreVertical,
  Eye,
  EyeOff,
  BarChart4,
  LineChart,
  PieChart,
  Table2,
  List,
  ListPlus,
  Code,
  AlertTriangle
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from "@/components/ui/select";
import { WidgetDefinition } from "@/components/admin/dashboard/widget-types";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle 
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface WidgetsListProps {
  widgets: WidgetDefinition[];
  isLoading: boolean;
  onSelect: (widget: WidgetDefinition) => void;
  onToggleActive: (id: string) => Promise<boolean>;
  onDelete: (id: string) => Promise<boolean>;
}

export function WidgetsList({ 
  widgets, 
  isLoading, 
  onSelect, 
  onToggleActive, 
  onDelete 
}: WidgetsListProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDashboard, setFilterDashboard] = useState<string>("all");
  const [filterActive, setFilterActive] = useState<string>("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [widgetToDelete, setWidgetToDelete] = useState<string | null>(null);
  
  useEffect(() => {
    console.log(`WidgetsList: Received ${widgets.length} widgets`);
  }, [widgets]);

  const filteredWidgets = widgets.filter(widget => {
    const matchesSearch = 
      widget.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      widget.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      widget.id.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesDashboard = 
      filterDashboard === "all" || 
      widget.dashboardType === filterDashboard || 
      (filterDashboard === "both" && widget.dashboardType === "both");
    
    const matchesActive = 
      filterActive === "all" || 
      (filterActive === "active" && widget.isActive) || 
      (filterActive === "inactive" && !widget.isActive);
    
    return matchesSearch && matchesDashboard && matchesActive;
  });

  const handleDeleteClick = (id: string) => {
    setWidgetToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (widgetToDelete) {
      await onDelete(widgetToDelete);
      setDeleteDialogOpen(false);
      setWidgetToDelete(null);
    }
  };

  const getChartIcon = (chartType: string) => {
    switch (chartType) {
      case 'bar':
        return <BarChart4 className="h-4 w-4" />;
      case 'line':
        return <LineChart className="h-4 w-4" />;
      case 'pie':
        return <PieChart className="h-4 w-4" />;
      case 'table':
        return <Table2 className="h-4 w-4" />;
      case 'list':
        return <List className="h-4 w-4" />;
      case 'stat':
        return <ListPlus className="h-4 w-4" />;
      case 'custom':
        return <Code className="h-4 w-4" />;
      default:
        return <BarChart4 className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search widgets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
        </div>
        <div className="flex gap-2">
          <Select
            value={filterDashboard}
            onValueChange={setFilterDashboard}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Dashboard" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Dashboards</SelectItem>
              <SelectItem value="admin">Admin Only</SelectItem>
              <SelectItem value="user">User Only</SelectItem>
              <SelectItem value="both">Both Dashboards</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filterActive}
            onValueChange={setFilterActive}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {widgets.length === 0 && !isLoading && (
        <Alert variant="warning" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No Widgets Found</AlertTitle>
          <AlertDescription>
            No widget definitions were found in the database. Try clicking the "Reset to Defaults" button to initialize widgets.
          </AlertDescription>
        </Alert>
      )}

      <ScrollArea className="h-[500px] rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Dashboard</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-4">
                  Loading widgets...
                </TableCell>
              </TableRow>
            ) : filteredWidgets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-4">
                  No widgets found.
                </TableCell>
              </TableRow>
            ) : (
              filteredWidgets.map((widget) => (
                <TableRow key={widget.id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col">
                      <span>{widget.title}</span>
                      <span className="text-xs text-muted-foreground truncate max-w-[220px]">
                        {widget.description}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {getChartIcon(widget.chartType)}
                      <span className="capitalize">{widget.chartType}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {widget.defaultSize}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={
                      widget.dashboardType === 'both'
                        ? 'secondary'
                        : widget.dashboardType === 'admin'
                        ? 'default'
                        : 'outline'
                    }>
                      {widget.dashboardType === 'both' ? 'Both' : 
                       widget.dashboardType === 'admin' ? 'Admin' : 'User'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={widget.isActive ? 'success' : 'destructive'}>
                      {widget.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onSelect(widget)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onToggleActive(widget.id)}>
                          {widget.isActive ? (
                            <>
                              <EyeOff className="mr-2 h-4 w-4" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <Eye className="mr-2 h-4 w-4" />
                              Activate
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDeleteClick(widget.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ScrollArea>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the widget and
              remove it from all dashboards where it's currently used.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
